import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  Circle,
  FileText,
  LifeBuoy,
  Mail,
  Receipt,
  Search,
  Settings,
  Workflow,
} from 'lucide-react';
import { APP_ROUTES } from '@config/routes';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { fetchPackages } from '@store/slices/packageSlice';
import { fetchEmailTemplates, fetchTrainingData } from '@store/slices/trainingDataSlice';
import { openModal } from '@store/slices/uiSlice';
import type { OnboardingStatus } from '@/hooks/useOnboardingStatus';
import { useModal } from '@/hooks/useModal';
import { StandardModal } from '@/modals/StandardModal';

interface OnboardingBannerProps {
  status: OnboardingStatus;
}

type TrackKey =
  | 'calendar'
  | 'email'
  | 'company-details'
  | 'currency-taxes'
  | 'payment-methods'
  | 'integrations'
  | 'invoicing'
  | 'packages'
  | 'contract-template'
  | 'questionnaire-template'
  | 'workflow'
  | 'ai-knowledge'
  | 'ai-email-templates';

interface ChecklistItem {
  key: string;
  label: string;
  description: string;
  href?: string;
  ctaLabel?: string;
  icon: typeof Calendar;
  trackKey?: TrackKey;
  isHighlight?: boolean;
  isOptional?: boolean;
  markCompleteOnClick?: TrackKey;
  action?: 'open-search';
}

interface ChecklistSection {
  title: string;
  items: ChecklistItem[];
}

const SNOOZE_KEY = 'drahft_onboarding_checklist_snooze_until';
const CHECKLIST_COMPLETION_KEY = 'drahft_onboarding_checklist_completion';
const DEFAULT_PACKAGE_IDS = new Set(['pkg-1', 'pkg-2', 'pkg-3', 'addon-1']);
const DEFAULT_CONTRACT_TEMPLATE_IDS = new Set(['ct-1']);
const DEFAULT_QUESTIONNAIRE_TEMPLATE_IDS = new Set(['qn-1']);
const SNOOZE_OPTIONS = [
  { label: 'Remind in 1 hour', hours: 1 },
  { label: 'Remind in 6 hours', hours: 6 },
  { label: 'Remind in 24 hours', hours: 24 },
];

const CHECKLIST_SECTIONS: ChecklistSection[] = [
  {
    title: 'Core Setup',
    items: [
      {
        key: 'calendar' as const,
        label: 'Connect Google Calendar',
        description: 'Required for booking confirmations and availability checks.',
        href: APP_ROUTES.SETTINGS_WITH_SECTION('date-time-calendar'),
        icon: Calendar,
        trackKey: 'calendar',
      },
      {
        key: 'email' as const,
        label: 'Configure Email (Gmail / SMTP)',
        description: 'Required for AI-drafted emails and client communication.',
        href: APP_ROUTES.EMAIL_SETUP,
        icon: Mail,
        trackKey: 'email',
      },
      {
        key: 'company-details',
        label: 'Company Details',
        description: 'Add your business profile, logo, and brand identity.',
        href: APP_ROUTES.SETTINGS_WITH_SECTION('company-details'),
        icon: Settings,
        trackKey: 'company-details',
      },
      {
        key: 'currency-taxes',
        label: 'Currency & Taxes',
        description: 'Configure your currency and tax defaults.',
        href: APP_ROUTES.SETTINGS_WITH_SECTION('currency-taxes'),
        icon: Settings,
        trackKey: 'currency-taxes',
      },
      {
        key: 'payment-methods',
        label: 'Payment Methods',
        description: 'Set how clients can pay you.',
        href: APP_ROUTES.SETTINGS_WITH_SECTION('payment-methods'),
        icon: Settings,
        trackKey: 'payment-methods',
      },
      {
        key: 'integrations',
        label: 'Integrations',
        description: 'Connect external tools and services you rely on.',
        href: APP_ROUTES.SETTINGS_WITH_SECTION('integrations'),
        icon: Settings,
        trackKey: 'integrations',
        markCompleteOnClick: 'integrations',
      },
    ],
  },
  {
    title: 'Invoicing',
    items: [
      {
        key: 'invoicing-home',
        label: 'Set up Invoicing',
        description: 'Configure invoice defaults and payment flow.',
        href: `${APP_ROUTES.TEMPLATES}?tab=invoice`,
        icon: Receipt,
        trackKey: 'invoicing',
        markCompleteOnClick: 'invoicing',
      },
      {
        key: 'invoicing-packages',
        label: 'Create a Package',
        description: 'Open Templates and create your first custom package.',
        href: `${APP_ROUTES.TEMPLATES}?tab=packages`,
        icon: Receipt,
        trackKey: 'packages',
      },
    ],
  },
  {
    title: 'Forms',
    items: [
      {
        key: 'forms-contract-template',
        label: 'Create Contract Template',
        description: 'Open Templates and create your first contract template.',
        href: `${APP_ROUTES.TEMPLATES}?tab=contracts`,
        icon: FileText,
        trackKey: 'contract-template',
      },
      {
        key: 'forms-questionnaire-template',
        label: 'Create Questionnaire Template',
        description: 'Open Templates and create your first questionnaire template.',
        href: `${APP_ROUTES.TEMPLATES}?tab=questionnaires`,
        icon: FileText,
        trackKey: 'questionnaire-template',
      },
    ],
  },
  {
    title: 'Workflow',
    items: [
      {
        key: 'workflow',
        label: 'Set up Workflow',
        description: 'Review and customize your automation workflow.',
        href: APP_ROUTES.WORKFLOW,
        icon: Workflow,
        trackKey: 'workflow',
        markCompleteOnClick: 'workflow',
      },
    ],
  },
  {
    title: 'AI Training Data',
    items: [
      {
        key: 'ai-knowledgebase',
        label: 'Knowledge Base',
        description: 'Open AI Training Data and fill your Knowledge Base.',
        href: `${APP_ROUTES.SETTINGS_WITH_SECTION('ai-training')}&tab=knowledge`,
        icon: Settings,
        trackKey: 'ai-knowledge',
      },
      {
        key: 'ai-email-templates',
        label: 'Email Templates',
        description: 'Open AI Training Data and create at least one email template.',
        href: `${APP_ROUTES.SETTINGS_WITH_SECTION('ai-training')}&tab=templates`,
        icon: Mail,
        trackKey: 'ai-email-templates',
      },
    ],
  },
  {
    title: 'Super Helpful Features',
    items: [
      {
        key: 'search-everywhere',
        label: 'Search Everywhere',
        description: 'Find pages, projects, contacts, messages, and more in one place.',
        ctaLabel: 'Open Search',
        icon: Search,
        action: 'open-search',
        isHighlight: true,
        isOptional: true,
      },
      {
        key: 'help-support',
        label: 'Help & Support',
        description: 'Use help articles and support if you get stuck.',
        href: APP_ROUTES.SUPPORT,
        icon: LifeBuoy,
        isHighlight: true,
        isOptional: true,
      },
    ],
  },
];

const readManualCompletion = (): Partial<Record<TrackKey, boolean>> => {
  try {
    const raw = localStorage.getItem(CHECKLIST_COMPLETION_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return parsed as Partial<Record<TrackKey, boolean>>;
  } catch {
    return {};
  }
};

const readStoredTemplateCount = (key: string) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return 0;
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return 0;
    }

    return parsed.length;
  } catch {
    return 0;
  }
};

export function OnboardingBanner({ status }: OnboardingBannerProps) {
  const dispatch = useAppDispatch();
  const checklistModal = useModal('onboarding.checklist');
  const userProfile = useAppSelector(state => state.auth.userProfile);
  const invoicePackages = useAppSelector(state => state.packages.packages);
  const trainingData = useAppSelector(state => state.trainingData.data);
  const emailTemplates = useAppSelector(state => state.trainingData.templates);
  const contractTemplates = useAppSelector(state => state.settingsTemplates.contractTemplates);
  const questionnaireTemplates = useAppSelector(
    state => state.settingsTemplates.questionnaireTemplates
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manualCompletion, setManualCompletion] = useState<Partial<Record<TrackKey, boolean>>>({});

  useEffect(() => {
    dispatch(fetchPackages(undefined));
    dispatch(fetchTrainingData(undefined));
    dispatch(fetchEmailTemplates());
  }, [dispatch]);

  useEffect(() => {
    setManualCompletion(readManualCompletion());
  }, []);

  const hasCompanyDetails = useMemo(() => {
    const company = userProfile?.fundamentalsSettings?.companyDetails;
    return Boolean(company?.companyName?.trim() && company?.companyEmail?.trim());
  }, [userProfile?.fundamentalsSettings?.companyDetails]);

  const hasCurrencyTaxes = useMemo(() => {
    const currencyTaxes = userProfile?.fundamentalsSettings?.currencyTaxes;
    return Boolean(currencyTaxes?.isSaved || currencyTaxes?.currency);
  }, [userProfile?.fundamentalsSettings?.currencyTaxes]);

  const hasPaymentMethods = useMemo(() => {
    const payment = userProfile?.fundamentalsSettings?.paymentMethods;
    return Boolean(
      payment?.stripeConnected ||
      payment?.stripeAccountId ||
      payment?.eTransferEnabled ||
      payment?.eTransferRecipient
    );
  }, [userProfile?.fundamentalsSettings?.paymentMethods]);

  const hasCustomPackage = useMemo(
    () => invoicePackages.some(pkg => !DEFAULT_PACKAGE_IDS.has(pkg.id)),
    [invoicePackages]
  );

  const hasCustomContractTemplate = useMemo(() => {
    const fromState = contractTemplates.some(
      template => !DEFAULT_CONTRACT_TEMPLATE_IDS.has(template.id)
    );

    if (fromState) {
      return true;
    }

    return readStoredTemplateCount('drahft_contract_templates') > 1;
  }, [contractTemplates]);

  const hasCustomQuestionnaireTemplate = useMemo(() => {
    const fromState = questionnaireTemplates.some(
      template => !DEFAULT_QUESTIONNAIRE_TEMPLATE_IDS.has(template.id)
    );

    if (fromState) {
      return true;
    }

    return readStoredTemplateCount('drahft_questionnaire_templates') > 1;
  }, [questionnaireTemplates]);

  const hasKnowledgeBaseData = useMemo(() => {
    const content = trainingData?.content;
    if (!content) {
      return false;
    }

    const fields = [
      content.business_info,
      content.products_services,
      content.pricing_packages,
      content.pricing_link,
      content.faq,
      content.contact_next_steps,
    ];

    return fields.some(value => typeof value === 'string' && value.trim().length > 0);
  }, [trainingData?.content]);

  const hasEmailTemplates = emailTemplates.length > 0;

  const markTrackKeyComplete = (trackKey: TrackKey) => {
    const nextCompletion = {
      ...manualCompletion,
      [trackKey]: true,
    };

    setManualCompletion(nextCompletion);

    try {
      localStorage.setItem(CHECKLIST_COMPLETION_KEY, JSON.stringify(nextCompletion));
    } catch {
      // no-op for environments where storage is unavailable
    }
  };

  const checklistWithState = useMemo(
    () =>
      CHECKLIST_SECTIONS.map(section => ({
        ...section,
        items: section.items.map(item => {
          if (!item.trackKey) {
            return {
              ...item,
              done: true,
            };
          }

          let done = false;

          if (item.trackKey === 'calendar') {
            done = status.calendarConnected;
          }

          if (item.trackKey === 'email') {
            done = status.emailConfigured;
          }

          if (item.trackKey === 'company-details') {
            done = hasCompanyDetails;
          }

          if (item.trackKey === 'currency-taxes') {
            done = hasCurrencyTaxes;
          }

          if (item.trackKey === 'payment-methods') {
            done = hasPaymentMethods;
          }

          if (item.trackKey === 'integrations') {
            done = Boolean(manualCompletion.integrations);
          }

          if (item.trackKey === 'invoicing') {
            done = Boolean(manualCompletion.invoicing);
          }

          if (item.trackKey === 'packages') {
            done = hasCustomPackage;
          }

          if (item.trackKey === 'contract-template') {
            done = hasCustomContractTemplate;
          }

          if (item.trackKey === 'questionnaire-template') {
            done = hasCustomQuestionnaireTemplate;
          }

          if (item.trackKey === 'workflow') {
            done = Boolean(manualCompletion.workflow);
          }

          if (item.trackKey === 'ai-knowledge') {
            done = hasKnowledgeBaseData;
          }

          if (item.trackKey === 'ai-email-templates') {
            done = hasEmailTemplates;
          }

          return {
            ...item,
            done,
          };
        }),
      })),
    [
      hasCompanyDetails,
      hasCurrencyTaxes,
      hasCustomContractTemplate,
      hasCustomPackage,
      hasCustomQuestionnaireTemplate,
      hasEmailTemplates,
      hasKnowledgeBaseData,
      hasPaymentMethods,
      manualCompletion,
      status.calendarConnected,
      status.emailConfigured,
    ]
  );

  const requiredItems = useMemo(
    () => checklistWithState.flatMap(section => section.items).filter(item => !item.isOptional),
    [checklistWithState]
  );

  const pendingCount = useMemo(
    () => requiredItems.filter(item => !item.done).length,
    [requiredItems]
  );

  const totalSteps = requiredItems.length;
  const completedCount = totalSteps - pendingCount;
  const completionPercent = Math.round((completedCount / Math.max(totalSteps, 1)) * 100);

  const isIncomplete = !status.isLoading && pendingCount > 0;

  useEffect(() => {
    if (!isIncomplete) {
      setIsModalOpen(false);
      return;
    }

    let snoozeUntil = 0;
    try {
      const raw = localStorage.getItem(SNOOZE_KEY);
      snoozeUntil = raw ? Number(raw) : 0;
    } catch {
      snoozeUntil = 0;
    }

    if (!Number.isFinite(snoozeUntil) || Date.now() > snoozeUntil) {
      setIsModalOpen(true);
    }
  }, [isIncomplete]);

  const handleSnooze = (hours = 6) => {
    try {
      const snoozeMs = 1000 * 60 * 60 * hours;
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + snoozeMs));
    } catch {
      // no-op for environments where storage is unavailable
    }
    checklistModal.close();
    setIsModalOpen(false);
  };

  const handleItemAction = (item: ChecklistItem) => {
    if (item.markCompleteOnClick) {
      markTrackKeyComplete(item.markCompleteOnClick);
    }

    if (item.action === 'open-search') {
      checklistModal.close();
      setIsModalOpen(false);
      dispatch(openModal({ id: 'global.search' }));
    }
  };

  const handleCloseModal = () => {
    handleSnooze();
  };

  return (
    <StandardModal
      isOpen={isModalOpen || checklistModal.isOpen}
      onClose={handleCloseModal}
      showCloseButton={false}
      title={
        <div className="flex items-start gap-3">
          <div className="w-full">
            <p className="text-base font-semibold leading-tight text-gray-900">
              Finish setting up your account and get 50 free chats
            </p>
            <p className="mt-1 text-sm font-normal text-gray-600">
              {completionPercent}% complete ({completedCount}/{totalSteps} steps)
            </p>
          </div>
        </div>
      }
      size="xl"
      panelClassName="max-h-[86vh]"
      bodyClassName="space-y-6 overflow-y-auto"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {SNOOZE_OPTIONS.map(option => (
            <button
              key={option.label}
              type="button"
              onClick={() => handleSnooze(option.hours)}
              className="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-neutral-dark hover:bg-neutral-800"
            >
              {option.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="w-full h-2 overflow-hidden bg-amber-100">
        <div
          className="h-full transition-[width] duration-300 bg-amber-500"
          style={{ width: `${completionPercent}%` }}
        />
      </div>

      {checklistWithState.map(section => (
        <section key={section.title} className="px-4 py-0">
          <h3 className="pb-2 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            {section.title}
          </h3>
          <ul className="space-y-2">
            {section.items.map(item => {
              const Icon = item.icon;

              const rowClassName = item.isHighlight
                ? 'border-blue-200 bg-blue-50'
                : 'border-gray-200 bg-gray-50';

              const titleClassName = item.done ? 'text-gray-400 line-through' : 'text-gray-900';

              return (
                <li key={item.key} className={`flex items-start gap-3 p-3 ${rowClassName}`}>
                  <span className="mt-0.5 shrink-0">
                    {item.done ? (
                      <CheckCircle size={16} className="text-emerald-500" />
                    ) : (
                      <Circle size={16} className="text-amber-400" />
                    )}
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${titleClassName}`}>{item.label}</p>
                    <p className="mt-0.5 text-xs text-gray-600">{item.description}</p>
                    {item.action === 'open-search' && (
                      <p className="mt-1 text-xs font-medium text-blue-700">
                        Tip: You can also open it from the sidebar Search.
                      </p>
                    )}
                  </div>

                  {item.href ? (
                    <Link
                      to={item.href}
                      onClick={() => {
                        if (item.markCompleteOnClick) {
                          markTrackKeyComplete(item.markCompleteOnClick);
                        }

                        checklistModal.close();
                        setIsModalOpen(false);
                      }}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-black"
                    >
                      <Icon size={12} />
                      {item.ctaLabel || 'Open'}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleItemAction(item)}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
                    >
                      <Icon size={12} />
                      {item.ctaLabel || 'Open'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </StandardModal>
  );
}
